import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { PosDevice } from './device';
import { Rasppi } from './rasppi';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PosDeviceType } from '../types';
import { RasppiType } from '../types';

const _schema = {
    name: 'PINPAD',
    fields: [
        { name: 'rasppi', type: 'objid' },
        { name: 'status', type: 'string', default: 'AC' },
        { name: 'name', type: 'string' },
        { name: 'host', type: 'string' },
        { name: 'port', type: 'number', default: '8887' },
        { name: 'apikey', type: 'string' },
        { name: 'posdevice', alias: 'device', type: 'objid' },
        { name: 'oncash', type: 'boolean', default: false },
        { name: 'oncard', type: 'boolean', default: false },
        { name: 'nocash', type: 'boolean', default: false },
    ],
    relate: [
        { direction: '>', target: 'RASPPI', by: 'pinpads', name: 'rasppi', reason: 'rasppi', child: false, class: RasppiType },
        { direction: '>', target: 'POSDEVICE', by: 'posdevice', name: 'device', reason: 'device', child: false, class: PosDeviceType },
    ]
} as const;

const _PinpadClass = CreateObject(_schema);
if (!_PinpadClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Pinpad extends _PinpadClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Pinpad {
        return this._Copy(store) as Pinpad;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'pinpads' && related instanceof Rasppi){
            if (!this.Commited){
                return;
            }

            if (related === this.rasppi){
                related.AddPinpad(this);
            }
            return;
        }

        if (relation === 'posdevice' && related instanceof PosDevice){
            if (related === this.device){
                related.AddPinpad(this);
            }
            else {
                related.DelPinpad(this);
            }
        }
    }

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if ((this.status == 'DE') && !this.Commited){
            this.rasppi?.DelPinpad(this);
            this.device?.DelPinpad(this);
            return;
        }

        if (this.rasppi){
            this.rasppi.DoRefresh(this.table);
        }
    }

    /** Active terminal: own row not deleted and parent print service valid. */
    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE') && !!this.rasppi?.IsValid);
    }

    /**
     * Display / Paytef identity label (`device.name` when linked, otherwise `name`).
     */
    get PinpadName(): string | null {
        return this.device?.name || this.name || null;
    }

    /**
     * PTF presence of the linked dataphone (`PosDevice.PtfConnected`).
     * Unlinked terminals are offline.
     */
    get Connected(): boolean {
        return this.device?.PtfConnected ?? false;
    }

    /**
     * Latest PTF session touch of the linked dataphone (`PosDevice.PtfTouch`).
     */
    get touch(): Date | null {
        return this.device?.PtfTouch ?? null;
    }
}
